// WaterfallChart.jsx
import { useResponsiveValue } from "@theme-ui/match-media";
import React, { useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  XAxis,
  YAxis,
  Tooltip,
  Bar,
  ReferenceLine,
} from "recharts";
import { Heading } from "theme-ui";

function buildData({ initial, steps, final, hideTiny = true, tiny = 1e-9 }) {
  const rows = [];
  rows.push({
    label: "Opening",
    base: 0,
    inc: Math.max(initial, 0),
    dec: Math.max(-initial, 0),
    type: "total",
    fill: "#D5D9DA",
  });

  let running = initial;
  for (const s of steps) {
    const v = Number(s.value || 0);
    if (hideTiny && Math.abs(v) < tiny) continue;
    const next = running + v;
    rows.push({
      label: s.label,
      base: Math.min(running, next),
      inc: v > 0 ? v : 0,
      dec: v < 0 ? -v : 0,
      type: "step",
    });
    running = next;
  }

  const finalVal = typeof final === "number" ? final : running;
  rows.push({
    label: "Closing",
    base: 0,
    inc: Math.max(finalVal, 0),
    dec: Math.max(-finalVal, 0),
    type: "total",
    fill: "#D5D9DA",
  });

  return rows;
}

const currency = (n) =>
  `$${n.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

/** Compute a “nice” tick step and explicit ticks */
function niceTicks(min, max, count = 5) {
  if (!isFinite(min) || !isFinite(max) || min === max) {
    const v = isFinite(min) ? min : 0;
    return [v, v + 1, v + 2, v + 3, v + 4];
  }
  const span = max - min;
  const step0 = span / Math.max(1, count - 1);
  const pow10 = Math.pow(10, Math.floor(Math.log10(step0)));
  const candidates = [1, 2, 2.5, 5, 10].map((m) => m * pow10);
  const step = candidates.reduce((best, s) =>
    Math.abs(step0 - s) < Math.abs(step0 - best) ? s : best
  );

  const niceMin = Math.floor(min / step) * step;
  const niceMax = Math.ceil(max / step) * step;

  const ticks = [];
  for (let v = niceMin; v <= niceMax + 0.5 * step; v += step) {
    ticks.push(v);
    if (ticks.length > 50) break;
  }
  return ticks;
}

// --- Custom tooltip that shows a single line ---
function WaterfallTooltip({ active, label, payload, valueFormatter }) {
  if (!active || !payload || payload.length === 0) return null;

  const row = payload[0]?.payload;

  // pick the non-zero delta for steps; otherwise show total value
  let title = "Change";
  let signedValue = 0;

  if (row?.type === "total") {
    title = "Total";
    signedValue = (row.inc || 0) - (row.dec || 0); // effectively the absolute total
  } else {
    if ((row?.inc ?? 0) !== 0) signedValue = row.inc;
    else if ((row?.dec ?? 0) !== 0) signedValue = -row.dec;
    else signedValue = 0;
  }

  return (
    <div
      style={{
        background: "#fff",
        border: "1px solid #E2E8F0",
        padding: "8px 12px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ color: signedValue >= 0 ? "#1AD3C1" : "#ED1E43" }}>
        {title}: {signedValue < 0 ? `(${valueFormatter(Math.abs(signedValue))})` : valueFormatter(Math.abs(signedValue))}
      </div>
    </div>
  );
}

export default function WaterfallChart({
  initial = 0,
  steps = [],
  final,
  height = 380,
  valueFormatter = currency,
  increaseFill = "#1AD3C1",
  decreaseFill = "#ED1E43",
  barSize = 200,
  reportDate,
}) {
  const isMobile = useResponsiveValue([true, true, true, false]);
  const xTickProps = isMobile
  ? { angle: -45, textAnchor: "end" }
  : { angle: 0,   textAnchor: "middle"};

  const rows = useMemo(
    () => buildData({ initial, steps, final }),
    [initial, steps, final]
  );

  const { yMin, yMax } = useMemo(() => {
    // Build cumulative levels
    const levels = [0];

    const opening = Number(initial || 0);
    levels.push(opening);

    let run = opening;
    for (const s of steps) {
      const v = Number(s?.value || 0);
      const next = run + v;
      levels.push(run, next);
      run = next;
    }

    // If a final value is provided, include it too
    if (typeof final === 'number') levels.push(final);

    const min = Math.min(...levels);
    const max = Math.max(...levels);

    // keep a tiny pad if you want:
    return { yMin: Math.floor(min), yMax: Math.ceil(max) };
  }, [initial, steps, final]);

  const ticks = useMemo(() => niceTicks(yMin, yMax, 6), [yMin, yMax]);

  const formattedReportDate = reportDate
    ? new Date(reportDate).toLocaleDateString(undefined, {
        year: "numeric",
        month: "long",
        day: "numeric",
      })
    : null;

  return (
    <div style={{ width: "100%", height, display: "flex", flexDirection: "column" }}>
      <Heading as="h4" variant="h4" sx={{ my: 2, mx: "auto" }}>
        {formattedReportDate
          ? `Quarterly Cash Flows - ${formattedReportDate} Report`
          : "Quarterly Cash Flows"}
      </Heading>
      <ResponsiveContainer>
        <ComposedChart
          data={rows}
          barGap={0}
          barCategoryGap={0}
          margin={{ top: 16, right: 16, bottom: 30, left: 64 }}
        >
          <XAxis
            dataKey="label"
            label={{
              value: "Quarterly Activities",
              position: "insideBottom",
              offset: -24,
              fill: "#00000A",
            }}
            interval={0}
            minTickGap={0}
            axisLine={false}
            tickLine={false}
            tick={xTickProps}
            height={isMobile ? 80 : 30}
          />
          <YAxis
            ticks={ticks}
            tickFormatter={(v) => valueFormatter(v)}
            domain={[ticks[0], ticks[ticks.length - 1]]}
            width={56}
            tickMargin={8}
            label={({ viewBox }) => {
              const { y, height } = viewBox;
              return (
                <text
                  x={0 - height / 2}
                  y={y}
                  transform="rotate(-90)"
                  textAnchor="middle"
                >
                  Cash Flow ($A'000)
                </text>
              );
            }}
          />
          <Tooltip content={<WaterfallTooltip valueFormatter={valueFormatter} />} />
          <Bar
            dataKey="base"
            stackId="wf"
            fill="rgba(0,0,0,0)"
            stroke="rgba(0,0,0,0)"
            isAnimationActive={false}
            barSize={barSize}
          />
          <Bar
            dataKey="inc"
            stackId="wf"
            name="Increase"
            fill={increaseFill}
            barSize={barSize}
          />
          <Bar
            dataKey="dec"
            stackId="wf"
            name="Decrease"
            fill={decreaseFill}
            barSize={barSize}
          />
          <ReferenceLine y={0} stroke="#999" strokeWidth={2} />
        </ComposedChart>
      </ResponsiveContainer>
    </div>
  );
}
